/* eslint-disable import/order */
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Routes, Route, Navigate } from 'react-router-dom'
import { HistoryRouter } from 'redux-first-history/rr6'

import MaintenanceScreenImage from '@/assets/NewSearchErrorImage.svg'
import { Toaster } from '@/components/ui/sonner'

import { AppRoutes } from './constants/appRoutes'
import { Error404 } from './components/ErrorState/Error404'
import { ReportInProgress } from './components/ReportInProgress/ReportInProgress'
import { BatchSearchInProgress } from './components/BatchSearchInProgress/BatchSearchInProgress'
import useTheme from './hooks/useSwitchTheme'
import { initializeSentry } from './initializeSentry'
import { ContactUs } from './pages/ContactUs/ContactUs'
import { FeedbackPayment } from './pages/Home/components/FeedbackPayment/FeedbackPayment'
import { FeedbackScreen } from './pages/Home/components/FeedbackScreen/FeedbackScreen'
import { Home } from './pages/Home/Home'
import { LeftSideBar } from './pages/LeftSideBar/LeftSideBar'
import { CreatePasswordForm } from './pages/Login/CreatePasswordForm'
import { ForgotPasswordForm } from './pages/Login/ForgotPasswordForm'
import { ForgotPasswordCheckYourInbox } from './pages/Login/ForgotPasswordCheckYourInbox'
import { InviteFirstStep } from './pages/Invite/InviteFirstStep'
import { InviteSecondStep } from './pages/Invite/InviteSecondStep'
import { Login } from './pages/Login/Login'
import { LoginForm } from './pages/Login/LoginForm'

import { FaceMatchResults } from './pages/Reports/FaceMatchResults'
import { Reports } from './pages/Reports/Reports'
import { AccountPage } from './pages/Settings/pages/AccountPage'
import { SecurityPage } from './pages/Settings/pages/SecurityPage'
import { ApiKeysPage } from './pages/Settings/pages/ApiKeysPage'
import { CompanyPage } from './pages/Settings/pages/CompanyPage'
import { ReportsPage } from './pages/Settings/pages/ReportsPage'
import { SettingsList } from './pages/Settings/SettingsList'
import { EmailVerification } from './pages/SignUp/EmailVerification'
import { SignUp } from './pages/SignUp/SignUp'
import { SignUpFinalStep } from './pages/SignUp/SignUpFinalStep'
import { SignUpFirstStep } from './pages/SignUp/SignUpFirstStep'
import { SignUpSecondStep } from './pages/SignUp/SignUpSecondStep'
import { SignUpStart } from './pages/SignUp/SignUpStart'
import { selectInMaintenance } from './store/api/Config/Config.selector'
import { history } from './store/store'
import './i18n/config'
import { InvitationExpired } from './pages/Invite/InvitationExpired'
import { FaceSearchFatherScreen } from './pages/FaceSearch/FaceSearchFatherScreen'
import { GeneratingReport } from './pages/Search/GeneratingReport'

import { Enterprise } from './pages/Enterprise/Enterprise'
import { TeamSelection } from './pages/Enterprise/TeamSelection'
import { WorkspaceCreation } from './pages/Enterprise/WorkspaceCreation'
import { WorkspaceCreationSecondStep } from './pages/Enterprise/WorkspaceCreationSecondStep'
import { WorkspaceCreationThirdStep } from './pages/Enterprise/WorkspaceCreationThirdStep'
import { WorkspaceSettings } from './pages/Enterprise/WorkspaceSettings'
import { Billing } from './pages/Billing/Billing'
import { RoleGuide } from './pages/Enterprise/RoleGuide'
import { CreateTeamFirstStep } from './pages/Enterprise/CreateTeamFirstStep'
import { CreateTeamSecondStep } from './pages/Enterprise/CreateTeamSecondStep'
import { DigitalReport } from './pages/DigitalReport/DigitalReport'
import { SocialMediaProfile } from './pages/DigitalReport/SocialMediaProfile'
import { BatchSearch } from './pages/BatchSearch/BatchSearch'
import { NewFollowUpReportUseCase } from './pages/FollowUpReport/NewFollowUpReportUseCase'
import { NewFollowUpReport } from './pages/FollowUpReport/NewFollowUpReport'
import { NewFollowUpReportConsent } from './pages/FollowUpReport/NewFollowUpReportConsent'
import { NewFollowUpReportLegalDisclaimer } from './pages/FollowUpReport/NewFollowUpReportLegalDisclaimer'
import { AuthSuccess } from './pages/Auth/AuthSuccess'
import { AuthError } from './pages/Auth/AuthError'
import { AddExtraDetails as RunReportAddExtraDetails } from './pages/RunReport/steps/AddExtraDetails/AddExtraDetails'
import { BehaviorFlags as RunReportBehaviorFlags } from './pages/RunReport/steps/BehaviorFlags/BehaviorFlags'
import { CompanyForm as RunReportCompanyForm } from './pages/RunReport/steps/CompanyForm/CompanyForm'
import { ConsentRequired as RunReportConsentRequired } from './pages/RunReport/steps/ConsentRequired/ConsentRequired'
import { SearchStarted as RunReportSearchStarted } from './pages/RunReport/steps/SearchStarted/SearchStarted'
import { SubjectForm as RunReportSubjectForm } from './pages/RunReport/steps/SubjectForm/SubjectForm'
import { Submit as RunReportSubmit } from './pages/RunReport/steps/Submit/Submit'
import { ConfirmSubjectDetails as RunReportConfirmSubjectDetails } from './pages/RunReport/steps/ConfirmSubjectDetails/ConfirmSubjectDetails'
import { RefineSearch as RunReportRefineSearch } from './pages/RunReport/steps/RefineSearch/RefineSearch'
import { SearchResults as RunReportSearchResults } from './pages/RunReport/steps/SearchResults/SearchResults'
import { SearchForm } from './pages/RunReport/steps/SearchForm/SearchForm'
import { StartSearch } from './pages/RunReport/steps/StartSearch/StartSearch'

initializeSentry()

const App: React.FC = () => {
  useTheme()
  const showMaintenanceScreen = useSelector(selectInMaintenance)
  const { t } = useTranslation()

  return (
    <>
      <HistoryRouter history={history}>
        {showMaintenanceScreen ? (
          <FeedbackScreen
            title={t('feedback.inMaintenanceScreen.title')}
            message={t('feedback.inMaintenanceScreen.message')}
            image={MaintenanceScreenImage}
            cardStyles={'shadow-none border-none h-screen w-screen'}
          />
        ) : (
          <Routes>
            <Route path="/" element={<Navigate to="/login/login-form" />} />

            <Route path="auth/success" element={<AuthSuccess />} />
            <Route path="auth/error" element={<AuthError />} />

            <Route path="login" element={<Login />}>
              <Route path="login-form" element={<LoginForm />} />
              <Route path="forgot-password" element={<ForgotPasswordForm />} />
              <Route path="create-password" element={<CreatePasswordForm />} />
              <Route
                path="forgot-pasword-check-your-inbox"
                element={<ForgotPasswordCheckYourInbox />}
              />
            </Route>

            <Route path="sign-up" element={<SignUp />}>
              <Route path="sign-up-start" element={<SignUpStart />} />
              <Route
                path="email-verification"
                element={<EmailVerification />}
              />
              <Route path="first-step" element={<SignUpFirstStep />} />
              <Route path="second-step" element={<SignUpSecondStep />} />
              <Route path="final-step" element={<SignUpFinalStep />} />
              <Route
                path="invitation-expired"
                element={<InvitationExpired />}
              />
              <Route path="invite-first-step" element={<InviteFirstStep />} />
              <Route path="invite-second-step" element={<InviteSecondStep />} />
            </Route>

            <Route path="dashboard" element={<LeftSideBar />}>
              <Route path="error-404" element={<Error404 />} />
              <Route path="feedback-screen" element={<FeedbackScreen />} />
              <Route path="home" index element={<Home />} />

              <Route path="support" element={<ContactUs />} />
              <Route path="settings">
                <Route index element={<SettingsList />} />
                <Route path="account" element={<AccountPage />} />
                <Route path="security" element={<SecurityPage />} />
                <Route path="company" element={<CompanyPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="api-keys" element={<ApiKeysPage />} />
              </Route>
              <Route path="feedback-payment" element={<FeedbackPayment />} />
              <Route path="reports" element={<Reports />} />
              <Route
                path="enterprise-reports"
                element={<Navigate to={AppRoutes.reports} replace />}
              />
              <Route path="enterprise" element={<Enterprise />} />
              <Route path="team-selection" element={<TeamSelection />} />
              <Route path="role-guide" element={<RoleGuide />} />
              <Route path="billing" element={<Billing />} />
              <Route
                path="work-space-settings"
                element={<WorkspaceSettings />}
              />
              <Route
                path="work-space-creation"
                element={<WorkspaceCreation />}
              />
              <Route
                path="work-space-creation-second-step"
                element={<WorkspaceCreationSecondStep />}
              />
              <Route
                path="work-space-creation-third-step"
                element={<WorkspaceCreationThirdStep />}
              />
              <Route
                path="create-team-first-step"
                element={<CreateTeamFirstStep />}
              />
              <Route
                path="create-team-second-step"
                element={<CreateTeamSecondStep />}
              />

              <Route path="face-match-results" element={<FaceMatchResults />} />
              <Route path="face-search" element={<FaceSearchFatherScreen />} />
              <Route
                path="social-media-profile"
                element={<SocialMediaProfile />}
              />

              <Route path="digital-report" element={<DigitalReport />} />
              <Route path="report-in-progress" element={<ReportInProgress />} />
              <Route
                path="batch-search-in-progress"
                element={<BatchSearchInProgress />}
              />
              <Route path="generating-report" element={<GeneratingReport />} />
              <Route path="batch-search" element={<BatchSearch />} />

              <Route path="run-report">
                <Route path="start-search" element={<StartSearch />} />
                <Route path="search-form" element={<SearchForm />} />
                <Route
                  path="search-results"
                  element={<RunReportSearchResults />}
                />
                <Route
                  path="confirm-subject-details"
                  element={<RunReportConfirmSubjectDetails />}
                />
                <Route
                  path="refine-search"
                  element={<RunReportRefineSearch />}
                />
                <Route path="subject-form" element={<RunReportSubjectForm />} />
                <Route path="company-form" element={<RunReportCompanyForm />} />
                <Route
                  path="add-extra-details"
                  element={<RunReportAddExtraDetails />}
                />
                <Route
                  path="behavior-flags"
                  element={<RunReportBehaviorFlags />}
                />
                <Route
                  path="consent-required"
                  element={<RunReportConsentRequired />}
                />
                <Route path="submit" element={<RunReportSubmit />} />
                <Route
                  path="search-started"
                  element={<RunReportSearchStarted />}
                />
              </Route>

              <Route path="new-follow-up">
                <Route path="" element={<NewFollowUpReport />} />
                <Route path="use-case" element={<NewFollowUpReportUseCase />} />
                <Route path="consent" element={<NewFollowUpReportConsent />} />
                <Route
                  path="legal-disclaimer"
                  element={<NewFollowUpReportLegalDisclaimer />}
                />
              </Route>
            </Route>

            <Route path="*" element={<Error404 />} />
          </Routes>
        )}
      </HistoryRouter>

      <Toaster richColors />
    </>
  )
}

export default App
